import { useCallback, useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Link, useParams } from "react-router-dom";

import { api, ApiFailure } from "../api/client";
import type {
  Call,
  CallAttempt,
  CallEvent,
  CallLeg,
  MediaStream,
  VoiceURLRequest,
  Recording,
  VoiceApp,
} from "../api/types";
import { useSession } from "../store/session";
import { DataTable } from "../components/data-table";
import {
  Badge, Button, Card, EmptyState, ErrorState, Field, Loading, PageHeader,
  inputClass,
} from "../components/ui";
import { MarkupEditor } from "../components/MarkupEditor";
import { STATUS_LABELS, StatusBadge, formatDateTime, isLive, useInterval } from "./Calls";

// Detalle de una llamada: lo que pasó, en qué orden y por qué terminó así.
//
// Las tres consultas van juntas porque las tres responden a la misma pregunta
// y mirarlas por separado obliga a cruzarlas a mano: el estado dice *qué*
// pasó, los intentos dicen *por qué* —la causa de colgado, el código SIP— y la
// línea de tiempo dice *cuándo*.
const POLL_INTERVAL_MS = 3000;

export function CallDetail() {
  const { callId = "" } = useParams();
  const can = useSession((s) => s.can);

  const [call, setCall] = useState<Call | null>(null);
  const [attempts, setAttempts] = useState<CallAttempt[]>([]);
  const [events, setEvents] = useState<CallEvent[]>([]);
  const [voiceRequests, setVoiceRequests] = useState<VoiceURLRequest[]>([]);
  const [streams, setStreams] = useState<MediaStream[]>([]);
  const [legs, setLegs] = useState<CallLeg[]>([]);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  // La aplicación de voz de la llamada, cuando la hay. Sirve para dos cosas
  // que la llamada sola no contesta: cómo se llama —el identificador no lo
  // dice— y de dónde salieron sus instrucciones.
  const [voiceApp, setVoiceApp] = useState<VoiceApp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [hangingUp, setHangingUp] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [modifying, setModifying] = useState(false);

  const load = useCallback(
    async (silent: boolean) => {
      if (!silent) {
        setError(null);
        setCall(null);
      }
      try {
        // En paralelo: son lecturas independientes y encadenarlas
        // multiplicaría la latencia de abrir la vista.
        const [detail, attemptRows, eventRows, voiceRows, streamRows, legRows, recordingRows] =
          await Promise.all([
            api.get<Call>(`/api/v1/calls/${callId}`),
            api.get<CallAttempt[]>(`/api/v1/calls/${callId}/attempts`),
            api.get<CallEvent[]>(`/api/v1/calls/${callId}/events`),
            api.get<VoiceURLRequest[]>(`/api/v1/calls/${callId}/voice-url-requests`),
            api.get<MediaStream[]>(`/api/v1/calls/${callId}/streams`),
            api.get<CallLeg[]>(`/api/v1/calls/${callId}/legs`),
            // Las grabaciones van con red: son opcionales en el servidor.
            //
            // Si el despliegue no tiene directorio de grabaciones, la ruta no
            // existe y devuelve 404. Dentro de un `Promise.all` eso tumbaría
            // la página entera y el usuario vería "no existe la llamada", que
            // es rotundamente falso. Un fallo aquí sólo debe quitar el panel.
            api
              .get<Recording[]>(`/api/v1/recordings?call_id=${callId}`)
              .catch(() => [] as Recording[]),
          ]);
        setCall(detail);
        setAttempts(attemptRows ?? []);
        setEvents(eventRows ?? []);
        setVoiceRequests(voiceRows ?? []);
        setStreams(streamRows ?? []);
        setLegs(legRows ?? []);
        setRecordings(recordingRows ?? []);

        // Aparte del `Promise.all`: depende del detalle y es prescindible. Una
        // aplicación borrada después de la llamada no puede dejar la vista sin
        // pintar.
        if (detail.voice_app_id !== null) {
          setVoiceApp(
            await api
              .get<VoiceApp>(`/api/v1/voice-apps/${detail.voice_app_id}`)
              .catch(() => null),
          );
        } else {
          setVoiceApp(null);
        }
        setError(null);
      } catch (err) {
        if (err instanceof ApiFailure && err.status === 404) {
          setNotFound(true);
          return;
        }
        setError(err instanceof ApiFailure ? err.error.message : "error inesperado");
      }
    },
    [callId],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  // Se refresca solo mientras la llamada puede cambiar.
  useInterval(() => void load(true), call && isLive(call.status) ? POLL_INTERVAL_MS : null);

  async function hangup() {
    setActionError(null);
    setHangingUp(true);
    try {
      await api.post(`/api/v1/calls/${callId}/hangup`);
      await load(true);
    } catch (err) {
      setActionError(err instanceof ApiFailure ? err.error.message : "no se pudo colgar");
    } finally {
      setHangingUp(false);
    }
  }

  // Las cuatro tablas del detalle. Van en `useMemo` por el mismo motivo que en
  // el resto del portal: un array nuevo por render reconstruye los modelos de
  // la tabla en cada render, y esta vista se refresca sola cada 3 s mientras la
  // llamada está viva.
  const columnasIntentos = useMemo<ColumnDef<CallAttempt, any>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "Intento",
        meta: { label: "Intento" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-600">{row.original.id}</span>
        ),
      },
      {
        id: "dial_status",
        accessorKey: "dial_status",
        header: "Resultado",
        meta: { label: "Resultado" },
        cell: ({ row }) =>
          row.original.dial_status ? (
            <Badge tone={row.original.dial_status === "answered" ? "good" : "warn"}>
              {row.original.dial_status}
            </Badge>
          ) : (
            <span className="text-slate-600">en curso</span>
          ),
      },
      {
        id: "hangup_cause",
        accessorKey: "hangup_cause",
        header: "Causa",
        meta: { label: "Causa" },
        cell: ({ row }) => <span className="text-slate-600">{row.original.hangup_cause ?? "—"}</span>,
      },
      {
        id: "sip_code",
        accessorKey: "sip_code",
        header: "SIP",
        meta: { label: "SIP", width: "5rem" },
        cell: ({ row }) => <span className="text-slate-600">{row.original.sip_code ?? "—"}</span>,
      },
      {
        id: "amd_status",
        accessorKey: "amd_status",
        header: "Contestadora",
        meta: { label: "Contestadora", hideOnMobile: true },
        cell: ({ row }) => (
          <AMDResult status={row.original.amd_status} cause={row.original.amd_cause} />
        ),
      },
      {
        id: "ringing_at",
        accessorKey: "ringing_at",
        header: "Timbró",
        meta: { label: "Timbró", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-slate-600">{formatDateTime(row.original.ringing_at)}</span>
        ),
      },
      {
        id: "answered_at",
        accessorKey: "answered_at",
        header: "Contestó",
        meta: { label: "Contestó", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-slate-600">{formatDateTime(row.original.answered_at)}</span>
        ),
      },
      {
        id: "ended_at",
        accessorKey: "ended_at",
        header: "Terminó",
        meta: { label: "Terminó", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-slate-600">{formatDateTime(row.original.ended_at)}</span>
        ),
      },
      {
        id: "answered_seconds",
        accessorKey: "answered_seconds",
        header: "Segundos",
        meta: { label: "Segundos", align: "right", width: "7rem" },
        cell: ({ row }) => (
          <span className="tabular-nums text-slate-600">{row.original.answered_seconds}</span>
        ),
      },
    ],
    [],
  );

  const columnasConsultas = useMemo<ColumnDef<VoiceURLRequest, any>[]>(
    () => [
      {
        id: "target",
        accessorKey: "target",
        header: "Destino",
        meta: { label: "Destino", width: "7rem" },
        cell: ({ row }) => (
          <Badge tone={row.original.target === "primary" ? "neutral" : "warn"}>
            {row.original.target === "primary" ? "Principal" : "Respaldo"}
          </Badge>
        ),
      },
      {
        id: "url",
        accessorFn: (request) => `${request.method} ${request.url}`,
        header: "URL",
        meta: { label: "URL" },
        cell: ({ row }) => (
          <span className="break-all font-mono text-xs text-slate-600">
            {row.original.method} {row.original.url}
          </span>
        ),
      },
      {
        id: "outcome",
        accessorKey: "outcome",
        header: "Resultado",
        meta: { label: "Resultado" },
        cell: ({ row }) => (
          <div className="text-xs">
            <OutcomeBadge outcome={row.original.outcome} status={row.original.status_code} />
            {row.original.error && (
              <div className="mt-1 max-w-xs break-words text-rose-300">{row.original.error}</div>
            )}
          </div>
        ),
      },
      {
        id: "latency_ms",
        accessorKey: "latency_ms",
        header: "Latencia",
        meta: { label: "Latencia", align: "right", width: "7rem" },
        cell: ({ row }) => (
          <span className="tabular-nums text-slate-600">{row.original.latency_ms} ms</span>
        ),
      },
      {
        id: "verbs",
        accessorFn: (request) => request.verbs.join(" "),
        header: "Verbos",
        enableSorting: false,
        meta: { label: "Verbos", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-600">
            {row.original.verbs.length > 0 ? row.original.verbs.join(" → ") : "—"}
          </span>
        ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Cuándo",
        meta: { label: "Cuándo", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">{formatDateTime(row.original.created_at)}</span>
        ),
      },
    ],
    [],
  );

  const columnasPatas = useMemo<ColumnDef<CallLeg, any>[]>(
    () => [
      {
        id: "destination",
        accessorKey: "destination",
        header: "Destino",
        meta: { label: "Destino" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-100">{row.original.destination ?? "—"}</span>
        ),
      },
      {
        id: "caller_id",
        accessorKey: "caller_id",
        header: "Caller ID",
        meta: { label: "Caller ID" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-600">{row.original.caller_id ?? "—"}</span>
        ),
      },
      {
        id: "trunk_id",
        accessorKey: "trunk_id",
        header: "Troncal",
        meta: { label: "Troncal", hideOnMobile: true },
        // Sin troncal quiere decir que la pata salió a una extensión interna
        // del nodo, no a la red.
        cell: ({ row }) =>
          row.original.trunk_id ? (
            <span className="font-mono text-xs text-slate-600">{row.original.trunk_id}</span>
          ) : (
            <span className="text-xs text-slate-600">interna</span>
          ),
      },
      {
        id: "dial_status",
        accessorKey: "dial_status",
        header: "Resultado",
        meta: { label: "Resultado" },
        cell: ({ row }) =>
          row.original.dial_status ? (
            <Badge tone={row.original.dial_status === "answer" ? "good" : "warn"}>
              {row.original.dial_status}
            </Badge>
          ) : (
            <span className="text-xs text-slate-600">en curso</span>
          ),
      },
      // Duración y conversación por separado: la duración incluye el timbrado y
      // la mide la propia llamada; la conversación sale de los eventos AMI y
      // todavía no se escribe, así que sale en blanco en lugar de un cero que
      // se leería como «no hablaron».
      {
        id: "dial_seconds",
        accessorKey: "dial_seconds",
        header: "Duración",
        meta: { label: "Duración", align: "right", width: "7rem" },
        cell: ({ row }) => (
          <span className="tabular-nums text-slate-600">{row.original.dial_seconds} s</span>
        ),
      },
      {
        id: "answered_seconds",
        accessorKey: "answered_seconds",
        header: "Conversación",
        meta: { label: "Conversación", align: "right", width: "8rem" },
        cell: ({ row }) => (
          <span className="tabular-nums text-slate-600">
            {row.original.answered_seconds > 0 ? `${row.original.answered_seconds} s` : "—"}
          </span>
        ),
      },
      {
        id: "time_limit_seconds",
        accessorKey: "time_limit_seconds",
        header: "Corte",
        meta: { label: "Corte", align: "right", hideOnMobile: true, width: "7rem" },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">
            {row.original.time_limit_seconds ? `${row.original.time_limit_seconds} s` : "sin corte"}
          </span>
        ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Cuándo",
        meta: { label: "Cuándo", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">{formatDateTime(row.original.created_at)}</span>
        ),
      },
    ],
    [],
  );

  const columnasStreams = useMemo<ColumnDef<MediaStream, any>[]>(
    () => [
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado", width: "8rem" },
        cell: ({ row }) => <StreamStatusBadge status={row.original.status} />,
      },
      {
        id: "ws_url",
        accessorKey: "ws_url",
        header: "Destino",
        meta: { label: "Destino" },
        cell: ({ row }) => (
          <span className="break-all font-mono text-xs text-slate-600">{row.original.ws_url}</span>
        ),
      },
      {
        id: "audio_format",
        accessorKey: "audio_format",
        header: "Formato",
        meta: { label: "Formato" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-600">
            {row.original.audio_format}
            {/* El track solo se muestra cuando no es el de siempre: un
                "inbound" en cada fila es ruido. */}
            {row.original.track !== "inbound" && (
              <div className="mt-1 text-slate-500">track {row.original.track}</div>
            )}
          </span>
        ),
      },
      {
        id: "stream_seconds",
        accessorKey: "stream_seconds",
        header: "Duración",
        meta: { label: "Duración", align: "right", width: "7rem" },
        cell: ({ row }) => (
          <span className="tabular-nums text-slate-600">{row.original.stream_seconds} s</span>
        ),
      },
      {
        id: "frames",
        accessorFn: (stream) => stream.frames_in + stream.frames_out,
        header: "Tramas",
        meta: { label: "Tramas", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">
            <span title="Del abonado hacia el tenant">↑ {row.original.frames_in}</span>
            {" / "}
            <span title="Del tenant hacia el abonado">↓ {row.original.frames_out}</span>
            {/* Un descarte no es un detalle: significa que el audio se perdió,
                y es dato de diagnóstico del tenant. */}
            {row.original.frames_dropped > 0 && (
              <div className="mt-1 text-amber-300">{row.original.frames_dropped} descartadas</div>
            )}
          </span>
        ),
      },
      {
        id: "close_reason",
        accessorKey: "close_reason",
        header: "Cierre",
        meta: { label: "Cierre" },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">
            {row.original.close_reason ? <code>{row.original.close_reason}</code> : "—"}
            {row.original.close_code != null && (
              <div className="mt-1 text-slate-500">WS {row.original.close_code}</div>
            )}
          </span>
        ),
      },
    ],
    [],
  );

  if (notFound) {
    return (
      <div>
        <PageHeader title="Llamada no encontrada" />
        <EmptyState
          title="No existe o no es de esta cuenta"
          description="Las llamadas de otra cuenta responden igual que las inexistentes, a propósito."
        />
        <div className="mt-4">
          <Link to="/calls" className="text-sm text-sky-300 underline decoration-sky-400/40 transition hover:text-sky-200">
            Volver a las llamadas
          </Link>
        </div>
      </div>
    );
  }

  if (call === null && error === null) return <Loading />;
  if (call === null) return <ErrorState message={error ?? ""} onRetry={() => void load(false)} />;

  const live = isLive(call.status);

  return (
    <div>
      <PageHeader
        title={`Llamada ${call.to}`}
        description={`${call.direction === "outbound" ? "Saliente" : "Entrante"} desde ${call.from}`}
        action={
          live && can("calls.manage") ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setModifying((v) => !v)}>
                {modifying ? "Cancelar" : "Modificar en vivo"}
              </Button>
              <Button variant="danger" onClick={() => void hangup()} disabled={hangingUp}>
                {hangingUp ? "Colgando…" : "Colgar"}
              </Button>
            </div>
          ) : undefined
        }
      />

      {actionError && (
        <Card className="mb-4 border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
          {actionError}
        </Card>
      )}

      {modifying && live && (
        <LiveModification
          callId={callId}
          onCancel={() => setModifying(false)}
          onApplied={() => {
            setModifying(false);
            void load(true);
          }}
        />
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-4 md:col-span-2">
          <h2 className="text-sm font-medium text-slate-100">Resumen</h2>
          <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <Detail label="Identificador" value={<span className="font-mono text-xs">{call.id}</span>} />
            <Detail label="Estado" value={<StatusBadge status={call.status} />} />
            <Detail label="De" value={<span className="font-mono text-xs">{call.from}</span>} />
            <Detail label="A" value={<span className="font-mono text-xs">{call.to}</span>} />
            <Detail label="Encolada" value={formatDateTime(call.queued_at)} />
            <Detail label="Terminada" value={formatDateTime(call.ended_at)} />
            <Detail
              label="Segundos contestados"
              value={call.answered_seconds > 0 ? `${call.answered_seconds} s` : "—"}
            />
            <Detail
              label="Segundos facturables"
              value={call.billable_seconds > 0 ? `${call.billable_seconds} s` : "—"}
            />
            <Detail label="Duración máxima" value={`${call.max_duration_seconds} s`} />
            <Detail
              label="Detección de contestadora"
              value={
                call.machine_detection
                  ? MACHINE_DETECTION_LABELS[call.machine_detection] ?? call.machine_detection
                  : call.amd_requested
                    ? "La de la aplicación de voz"
                    : "No"
              }
            />
            <Detail
              label="Motivo de fin"
              value={call.end_reason ? <code className="text-xs">{call.end_reason}</code> : "—"}
            />
            {call.voice_url && (
              <Detail
                label="Voice URL"
                value={<span className="break-all font-mono text-xs">{call.voice_url}</span>}
              />
            )}
            {call.voice_app_id && (
              <Detail
                label="Aplicación de voz"
                value={
                  <>
                    {voiceApp !== null && <div>{voiceApp.name}</div>}
                    <span className="font-mono text-xs text-slate-500">{call.voice_app_id}</span>
                  </>
                }
              />
            )}
          </dl>
        </Card>

        <Card className="p-4">
          <h2 className="text-sm font-medium text-slate-100">Metadatos</h2>
          <p className="mt-1 text-xs text-slate-500">
            Lo que la cuenta mandó al originar. La plataforma no lo interpreta: vuelve tal cual en
            los webhooks.
          </p>
          {Object.keys(call.metadata ?? {}).length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">Sin metadatos.</p>
          ) : (
            <dl className="mt-3 space-y-1 text-sm">
              {Object.entries(call.metadata).map(([key, value]) => (
                <div key={key} className="flex gap-2">
                  <dt className="font-mono text-xs text-slate-500">{key}</dt>
                  <dd className="font-mono text-xs text-slate-100">{String(value)}</dd>
                </div>
              ))}
            </dl>
          )}
        </Card>
      </div>

      {recordings.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-medium text-slate-100">Grabación</h2>
          <RecordingPanel recordings={recordings} />
        </section>
      )}

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-slate-100">Intentos</h2>
        <p className="mb-3 text-xs text-slate-500">
          Incluye los fallidos: es donde está el diagnóstico de por qué una llamada no entró.
        </p>
        <DataTable<CallAttempt>
          data={attempts}
          columns={columnasIntentos}
          getRowId={(attempt) => attempt.id}
          caption="Intentos de la llamada"
          empty={{
            title: "Sin intentos",
            description: "La llamada todavía no ha salido a la red.",
          }}
        />
      </section>

      {/* De dónde salieron las instrucciones. Son dos cosas distintas y la
          pantalla no las puede mezclar: una aplicación con markup guardado no
          sale a la red, así que no tiene consultas que enseñar, y pintarle
          «la llamada no llegó a pedir instrucciones» la haría parecer rota. */}
      {voiceApp?.source === "markup" && voiceRequests.length === 0 ? (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-medium text-slate-100">Instrucciones</h2>
          <p className="mb-3 max-w-prose text-xs text-slate-500">
            Esta llamada se ejecutó con el markup guardado de «{voiceApp.name}»: la
            plataforma no consultó ninguna Voice URL porque la aplicación lleva sus
            instrucciones dentro.
          </p>
          <Card className="p-4">
            <pre className="overflow-x-auto whitespace-pre text-xs text-slate-300">
              {voiceApp.markup}
            </pre>
          </Card>
        </section>
      ) : (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-medium text-slate-100">Consultas a la Voice URL</h2>
          <p className="mb-3 text-xs text-slate-500">
            Lo que se pidió, cuánto tardó y qué verbos vino. La URL va sin los
            parámetros de consulta: pueden llevar datos de la cuenta.
          </p>
          <DataTable<VoiceURLRequest>
            data={voiceRequests}
            columns={columnasConsultas}
            getRowId={(request) => request.id}
            caption="Consultas a la Voice URL"
            empty={{
              title: "Sin consultas",
              description: "La llamada no llegó a pedir instrucciones.",
            }}
          />
        </section>
      )}

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-slate-100">Transferencias</h2>
        <p className="mb-3 text-xs text-slate-500">
          Una fila por cada <code>&lt;Dial&gt;</code>: a dónde se marcó, con qué
          caller ID y cómo acabó. Una fila sin cerrar es una transferencia que
          se intentó y de la que no se supo el resultado. La duración incluye el
          timbrado; no es tiempo facturable.
        </p>
        <DataTable<CallLeg>
          data={legs}
          columns={columnasPatas}
          getRowId={(leg) => leg.id}
          caption="Transferencias de la llamada"
          empty={{
            title: "Sin transferencias",
            description: "Esta llamada no ejecutó ningún <Dial>.",
          }}
        />
      </section>

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-slate-100">Media streams</h2>
        <DataTable<MediaStream>
          data={streams}
          columns={columnasStreams}
          getRowId={(stream) => stream.id}
          caption="Media streams de la llamada"
          empty={{
            title: "Sin streams",
            description: "Esta llamada no ejecutó ningún <Connect><Stream>.",
          }}
        />
      </section>

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-slate-100">Línea de tiempo</h2>
        {events.length === 0 ? (
          <EmptyState title="Sin eventos" />
        ) : (
          <Card className="divide-y divide-white/5">
            {events.map((event) => (
              <div key={event.id} className="flex flex-wrap items-baseline gap-3 px-4 py-3">
                <span className="w-40 shrink-0 text-xs text-slate-500">
                  {formatDateTime(event.occurred_at)}
                </span>
                <code className="text-xs font-medium text-slate-100">{event.type}</code>
                <span className="text-xs text-slate-600">{describeEvent(event)}</span>
              </div>
            ))}
          </Card>
        )}
      </section>
    </div>
  );
}

/**
 * StreamStatusBadge pinta el estado de un media stream.
 *
 * `authorized` y `connecting` no son estados "en marcha" para el operador:
 * significan que el stream se autorizó y el audio todavía no fluye, y
 * distinguirlos de `active` es lo que explica una llamada muda.
 */
/**
 * RecordingPanel reproduce y ofrece la grabación.
 *
 * El reproductor apunta a la liga firmada tal cual la dio la API. Eso funciona
 * precisamente porque la descarga **no** lleva API key: un `<audio>` no puede
 * añadir cabeceras, y por eso la autorización vive en la firma de la query
 * string (`internal/recordings/links.go`).
 *
 * La consecuencia es que la liga caduca, y hay que decirlo: si el usuario deja
 * esta pestaña abierta una hora, el reproductor dejará de funcionar y la única
 * explicación sensata es «recarga». Por eso se muestra la hora de caducidad en
 * lugar de dejar que falle en silencio.
 */
function RecordingPanel({ recordings }: { recordings: Recording[] }) {
  return (
    <Card className="divide-y divide-white/5">
      {recordings.map((rec) => (
        <div key={rec.id} className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <code className="font-mono text-xs text-slate-400">{rec.id}</code>
            <Badge
              tone={
                rec.status === "completed"
                  ? "good"
                  : rec.status === "recording"
                    ? "warn"
                    : rec.status === "failed"
                      ? "bad"
                      : "neutral"
              }
            >
              {ESTADO_DE_GRABACION[rec.status]}
            </Badge>
            {rec.duration_seconds !== null && (
              <span className="text-xs text-slate-500">{rec.duration_seconds} s</span>
            )}
            {rec.bytes !== null && (
              <span className="text-xs text-slate-600">{formatBytes(rec.bytes)}</span>
            )}
          </div>

          {rec.recording_url !== null ? (
            <>
              <audio controls preload="none" src={rec.recording_url} className="mt-3 w-full">
                Tu navegador no reproduce audio.
              </audio>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <a
                  href={rec.recording_url}
                  download={`${rec.id}.wav`}
                  className="text-xs font-medium text-sky-300 underline-offset-2 hover:underline"
                >
                  Descargar el WAV
                </a>
                {rec.recording_url_expires_at !== null && (
                  <span className="text-xs text-slate-600">
                    La liga caduca a las {formatDateTime(rec.recording_url_expires_at)}; recarga
                    esta página para obtener otra.
                  </span>
                )}
              </div>
            </>
          ) : (
            <p className="mt-2 text-xs text-slate-500">
              {rec.status === "recording"
                ? "La llamada sigue en pie: el archivo se cierra al colgar."
                : rec.status === "purged"
                  ? "La retención ya borró el audio. La fila queda como constancia de que existió."
                  : "No hay archivo que descargar."}
            </p>
          )}
        </div>
      ))}
    </Card>
  );
}

const ESTADO_DE_GRABACION: Record<Recording["status"], string> = {
  recording: "grabando",
  completed: "lista",
  failed: "falló",
  purged: "borrada",
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StreamStatusBadge({ status }: { status: MediaStream["status"] }) {
  const tones: Record<MediaStream["status"], "good" | "bad" | "warn" | "neutral"> = {
    authorized: "neutral",
    connecting: "warn",
    active: "good",
    stopped: "neutral",
    failed: "bad",
  };
  const labels: Record<MediaStream["status"], string> = {
    authorized: "Autorizado",
    connecting: "Conectando",
    active: "Activo",
    stopped: "Terminado",
    failed: "Falló",
  };
  return <Badge tone={tones[status]}>{labels[status]}</Badge>;
}

/**
 * LiveModification interrumpe lo que la llamada está haciendo y aplica algo
 * nuevo (ADR-004).
 *
 * Se piden una URL **o** un documento en línea, no los dos: con los dos no
 * habría forma de saber cuál gana, y adivinarlo significaría que el operador
 * descubre la respuesta en una llamada de verdad.
 *
 * El aviso de que la llamada se interrumpe no es decorativo: el prospecto está
 * escuchando, y lo que esté sonando se corta en cuanto se aplica.
 */
function LiveModification({
  callId,
  onCancel,
  onApplied,
}: {
  callId: string;
  onCancel: () => void;
  onApplied: () => void;
}) {
  const [mode, setMode] = useState<"voice_url" | "markup">("voice_url");
  const [voiceURL, setVoiceURL] = useState("");
  const [markup, setMarkup] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/api/v1/calls/${callId}`,
        mode === "voice_url" ? { voice_url: voiceURL } : { markup });
      onApplied();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "no se pudo modificar");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card className="mb-4 border-amber-500/30 bg-amber-500/10 p-4">
      <h2 className="text-sm font-medium text-amber-200">Modificar la llamada en curso</h2>
      <p className="mt-1 text-xs text-amber-200">
        Lo que esté sonando se interrumpe en cuanto se aplica, y la llamada
        continúa con lo nuevo.
      </p>

      <form className="mt-4 space-y-4" onSubmit={onSubmit}>
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={mode === "voice_url"}
              onChange={() => setMode("voice_url")}
            />
            Nueva Voice URL
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={mode === "markup"}
              onChange={() => setMode("markup")}
            />
            Markup en línea
          </label>
        </div>

        {mode === "voice_url" ? (
          <Field
            label="Voice URL"
            hint="Debe ser https y resolver a una dirección pública."
            error={error ?? undefined}
          >
            <input
              className={inputClass}
              type="url"
              required
              placeholder="https://cliente.example.com/nuevo"
              value={voiceURL}
              onChange={(e) => setVoiceURL(e.target.value)}
            />
          </Field>
        ) : (
          <>
            <MarkupEditor value={markup} onChange={setMarkup} rows={8} />
            {error && <p className="text-xs text-rose-300">{error}</p>}
          </>
        )}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Aplicando…" : "Aplicar"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * OutcomeBadge traduce el desenlace de una consulta.
 *
 * Los nombres se separan a propósito: un timeout es el servidor del tenant
 * tardando, un error de red es su servidor inalcanzable y un bloqueo es su URL
 * apuntando a donde no debe. Cada uno se arregla en un sitio distinto, y un
 * solo "falló" los mandaría a buscar al equivocado.
 */
function OutcomeBadge({
  outcome,
  status,
}: {
  outcome: VoiceURLRequest["outcome"];
  status: number | null;
}) {
  const labels: Record<VoiceURLRequest["outcome"], string> = {
    ok: "Correcta",
    timeout: "Sin respuesta a tiempo",
    network_error: "No se pudo conectar",
    http_error: `Respondió ${status ?? "error"}`,
    invalid_markup: "Markup inválido",
    blocked: "Destino bloqueado",
    too_large: "Respuesta demasiado grande",
  };
  return (
    <Badge tone={outcome === "ok" ? "good" : "bad"}>{labels[outcome]}</Badge>
  );
}

// Las etiquetas son de los modos que acepta la API y la columna
// `calls.machine_detection`: `off`, `enable` y `detect_message_end`. Antes
// decían `disabled` y `detect`, que no existen en ningún lado, así que una
// llamada con detección mostraba el valor crudo.
const MACHINE_DETECTION_LABELS: Record<string, string> = {
  off: "No",
  enable: "Al contestar",
  detect_message_end: "Al terminar el saludo del buzón",
};

/**
 * AMDResult muestra la clasificación de AMD con su causa.
 *
 * La causa se muestra siempre que exista, y no solo cuando el resultado es
 * dudoso, porque es lo que distingue un `machine` fiable de uno que se decidió
 * por agotar el tiempo de análisis. `notsure` se pinta en ámbar y no en rojo:
 * no es un error, es que no se pudo decidir, y la política ante contestadora no
 * se aplicó.
 */
function AMDResult({ status, cause }: { status: string | null; cause: string | null }) {
  if (!status) return <span className="text-xs text-slate-600">—</span>;

  const tone = status === "human" ? "good" : status === "machine" ? "warn" : "neutral";

  return (
    <div>
      <Badge tone={tone}>{AMD_LABELS[status] ?? status}</Badge>
      {cause && <div className="mt-1 font-mono text-[11px] text-slate-500">{cause}</div>}
    </div>
  );
}

const AMD_LABELS: Record<string, string> = {
  human: "Persona",
  machine: "Contestadora",
  notsure: "No se pudo decidir",
  hangup: "Colgó durante el análisis",
};

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-slate-100">{value}</dd>
    </div>
  );
}

/**
 * describeEvent resume el payload de un evento en una línea.
 *
 * Se resume en lugar de volcar el JSON porque una línea de tiempo con nueve
 * bloques de JSON no se lee: lo que hace falta de un vistazo es el estado y la
 * causa, y el resto está en la tabla de intentos.
 */
function describeEvent(event: CallEvent): string {
  const data = event.data ?? {};
  const parts: string[] = [];

  const status = data.status;
  if (typeof status === "string" && status in STATUS_LABELS) {
    parts.push(STATUS_LABELS[status as keyof typeof STATUS_LABELS]);
  }
  if (typeof data.dial_status === "string") parts.push(`resultado ${data.dial_status}`);
  if (typeof data.hangup_cause === "number") parts.push(`causa ${data.hangup_cause}`);
  if (typeof data.sip_code === "number") parts.push(`SIP ${data.sip_code}`);
  if (typeof data.error_code === "string") parts.push(data.error_code);

  return parts.join(" · ");
}
